import { fetchDepositOffers } from '@rushplay/api-client';
import { pipe, append, aperture, findIndex, replace, defaultTo, filter, pick, concat, mergeAll, map, reject, isNil, flatten, assoc, propOr, sort, ascend, prop, find, path, head, findLastIndex, gte, length, min, max, last, dropLast, propEq } from 'ramda';
import kebabCase from 'lodash.kebabcase';
import { SESSION_EXPIRED } from '@rushplay/websockets';

/**
 * @constant
 * @memberof rushplay/offers
 */
var DEPOSIT_OFFERS_UPDATED = 'offers/DEPOSIT_OFFERS_UPDATED';

/**
 * @constant
 * @memberof rushplay/offers
 */
var CLEARED = 'offers/CLEARED';

/**
 * Stores deposit-offers to state
 * @memberof rushplay/offers
 * @param {Object} payload Offers
 * @returns {Object} Redux action
 */
function updateDepositOffers(payload) {
  return {
    type: DEPOSIT_OFFERS_UPDATED,
    payload: payload,
    error: payload instanceof Error
  };
}

/**
 * Clears deposit-offers state
 * @memberof rushplay/offers
 * @returns {Object} Redux action
 */
function clear() {
  return {
    type: CLEARED
  };
}

/**
 * Fetches and stores deposit offers
 * @memberof rushplay/offers
 * @param {Object} params
 * @param {string} [params.affiliateId]
 * @returns {Object} Redux action
 */
function fetchDepositOffers$1() {
  var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  return fetchDepositOffers(params, {
    success: function success(res) {
      return updateDepositOffers(res.value.result);
    },
    failure: function failure() {
      return updateDepositOffers(new Error('offers.errors.failed-loading'));
    },
    version: 2
  });
}

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

/**
 * Gets index of offer in range of amount passed
 * @memberof rushplay/offers
 * @param {number} amount amount in cents
 * @param {Array} arr array of items
 * @returns {number} index of an offer in range
 */
function getValueIndexInRange(amount, arr) {
  return pipe(append(Infinity), aperture(2), findIndex(function (_ref) {
    var _ref2 = slicedToArray(_ref, 2),
        min$$1 = _ref2[0],
        max$$1 = _ref2[1];

    return amount >= min$$1 && amount < max$$1;
  }))(arr);
}

// For now I'm just moving this code to offers as it shouldn't be part of
// promotions anymore.
// TODO: see if we can improve it now that it's offers specific
var REWARD_TYPES = ['bonusMoneyPercent', 'maxFixedBonusMoney', 'maxFixedFreeGamesVoucher', 'maxFixedFreespins', 'maxFixedFeatureTriggers'];

function getOfferTranslationKey(type) {
  return 'deposit-offer.' + kebabCase(replace(/maxFixed/, '', type));
}

function getOfferRewardValueKey(rewardType, maxReward) {
  return {
    bonusMoneyPercent: 'bonusMoneyPercentCalculated',
    maxFixedFreeGamesVoucher: 'fixedFreeGamesVoucher',
    maxFixedFreespins: 'fixedFreespins',
    maxFixedBonusMoney: maxReward ? 'maxFixedBonusMoney' : 'fixedBonusMoney',
    maxFixedFeatureTriggers: 'fixedFeatureTriggers'
  }[rewardType];
}

function getRewardValue(offer, rewardType, maxReward) {
  var key = getOfferRewardValueKey(rewardType, maxReward);
  return defaultTo(0, offer[key]);
}

function getOfferReward(offer, type) {
  return {
    key: getOfferTranslationKey(type),
    value: getRewardValue(offer, type),
    translationVariables: defineProperty({}, getOfferRewardValueKey(type, true), getRewardValue(offer, type, true))
  };
}

/**
 * Gets maximum rewards for an offer
 * @memberof rushplay/offers
 * @param {Array} offer array of rewards
 * @returns {Object} maximum rewards for this offer
 */
function getMaxRewardsTranslationVariables(offer) {
  return filter(function (reward) {
    return reward != null && reward != 0;
  }, pick(concat(REWARD_TYPES, ['bonusMoneyPercentMaxCents', 'rangeLimits']), mergeAll(offer)));
}

/**
 * Gets rewards for offers
 * @memberof rushplay/offers
 * @param {Array} offers array of offers
 * @returns {Array} array of all reward translation keys and translation variables
 */
function getOfferRewards(offers) {
  return pipe(map(function (offer) {
    return pipe(reject(function (reward) {
      return isNil(offer[reward]) || !offer[reward];
    }), map(function (type) {
      return getOfferReward(offer, type);
    }))(REWARD_TYPES);
  }), flatten)(offers);
}

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments[1];

  if (action.error) {
    return state;
  }

  switch (action.type) {
    case DEPOSIT_OFFERS_UPDATED:
      {
        return assoc(['depositOffers'], action.payload, state);
      }

    case SESSION_EXPIRED:
    case CLEARED:
      {
        return {};
      }

    default:
      {
        return state;
      }
  }
}

function defaultEqualityCheck(a, b) {
  return a === b;
}

function areArgumentsShallowlyEqual(equalityCheck, prev, next) {
  if (prev === null || next === null || prev.length !== next.length) {
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  var length$$1 = prev.length;
  for (var i = 0; i < length$$1; i++) {
    if (!equalityCheck(prev[i], next[i])) {
      return false;
    }
  }

  return true;
}

function defaultMemoize(func) {
  var equalityCheck = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultEqualityCheck;

  var lastArgs = null;
  var lastResult = null;
  // we reference arguments instead of spreading them for performance reasons
  return function () {
    if (!areArgumentsShallowlyEqual(equalityCheck, lastArgs, arguments)) {
      // apply arguments instead of spreading for performance.
      lastResult = func.apply(null, arguments);
    }

    lastArgs = arguments;
    return lastResult;
  };
}

function getDependencies(funcs) {
  var dependencies = Array.isArray(funcs[0]) ? funcs[0] : funcs;

  if (!dependencies.every(function (dep) {
    return typeof dep === 'function';
  })) {
    var dependencyTypes = dependencies.map(function (dep) {
      return typeof dep;
    }).join(', ');
    throw new Error('Selector creators expect all input-selectors to be functions, ' + ('instead received the following types: [' + dependencyTypes + ']'));
  }

  return dependencies;
}

function createSelectorCreator(memoize) {
  for (var _len = arguments.length, memoizeOptions = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    memoizeOptions[_key - 1] = arguments[_key];
  }

  return function () {
    for (var _len2 = arguments.length, funcs = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      funcs[_key2] = arguments[_key2];
    }

    var recomputations = 0;
    var resultFunc = funcs.pop();
    var dependencies = getDependencies(funcs);

    var memoizedResultFunc = memoize.apply(undefined, [function () {
      recomputations++;
      // apply arguments instead of spreading for performance.
      return resultFunc.apply(null, arguments);
    }].concat(memoizeOptions));

    // If a selector is called with the exact same arguments we don't need to traverse our dependencies again.
    var selector = memoize(function () {
      var params = [];
      var length$$1 = dependencies.length;

      for (var i = 0; i < length$$1; i++) {
        // apply arguments instead of spreading and mutate a local list of params for performance.
        params.push(dependencies[i].apply(null, arguments));
      }

      // apply arguments instead of spreading for performance.
      return memoizedResultFunc.apply(null, params);
    });

    selector.resultFunc = resultFunc;
    selector.dependencies = dependencies;
    selector.recomputations = function () {
      return recomputations;
    };
    selector.resetRecomputations = function () {
      return recomputations = 0;
    };
    return selector;
  };
}

var createSelector = createSelectorCreator(defaultMemoize);

/**
 * Gets offers
 * @private
 * @param {Object} state Offers state
 * @returns {Array} offers
 */
function _getOffers(state, props) {
  return pipe(propOr([], 'depositOffers'), sort(ascend(prop('maxDepositNumber'))), find(function (offer) {
    return props.depositNumber < offer.maxDepositNumber;
  }), defaultTo({ packages: [] }), prop('packages'))(state);
}

/**
 * Gets amountcents from props
 * @private
 * @param {Object} props
 * @returns {number} amountCents
 */
function _getAmountCents(_, props) {
  return props.amountCents;
}

/**
 * Gets choice from props
 * @private
 * @param {Object} props
 * @returns {number} choice
 */
function _getChoice(_, props) {
  return props.choice;
}

function _getIsCampaign(_, props) {
  return props.isCampaign;
}

/**
 * Gets calculated offers based on amount
 * @private
 * @param {Array} offers
 * @param {number} amountCents amount to base calculation on
 * @returns {Array} contains calculated offers
 */
function _getCalculatedOffers(offers, amountCents, isCampaign) {
  function getCurrentReward(field, offer) {
    return path([field, getValueIndexInRange(amountCents, offer.rangeLimits)], offer);
  }

  return map(function (offer) {
    var rangeLimits = defaultTo([], offer.rangeLimits);
    // validMoneyCents is added so calculations are only carried out if they'll actually give the user a reward
    var validMoneyCents = amountCents >= head(rangeLimits) ? amountCents : 0;
    var bonusMoneyCents = validMoneyCents * (offer.bonusMoneyPercent / 100);

    // helper constants for progress calculations
    var rewardIndex = findLastIndex(gte(amountCents), rangeLimits);
    var percentPerStep = 100 / (isCampaign ? length(rangeLimits) - 1 : length(rangeLimits));
    // calculates progress in percent how close a deposit is to claiming the full rewards (Bonus percent)
    // For creating a progressbar where offer is rendered
    var bonusMoneyPercentProgressPercent = offer.bonusMoneyPercentMaxCents && min(100 / (offer.bonusMoneyPercentMaxCents / validMoneyCents), 100);
    // same as above but for offers where there's fixed rewards and/or rangeLimits setup
    var fixedOfferProgressPercent = min(Math.floor(percentPerStep * (rewardIndex + 1)), 100);
    // calculates what the minimum deposit to claim all rewards is
    // For a disclaimer-text in conjunction to the offer. A way to entice a greater deposit.
    var minDepositForFullBonusPercent = defaultTo(0, offer.bonusMoneyPercentMaxCents / (offer.bonusMoneyPercent / 100));

    return {
      id: offer.id,
      rangeLimits: rangeLimits,
      minDepositForFullRewards: max(minDepositForFullBonusPercent, last(isCampaign ? dropLast(1, rangeLimits) : rangeLimits)),
      offerProgressPercent: bonusMoneyPercentProgressPercent ? bonusMoneyPercentProgressPercent : fixedOfferProgressPercent,
      inLimits: gte(amountCents, head(rangeLimits)),
      bonusMoneyPercent: offer.bonusMoneyPercent,
      bonusMoneyPercentMaxCents: offer.bonusMoneyPercentMaxCents,
      bonusMoneyPercentCalculated: Math.min(bonusMoneyCents, offer.bonusMoneyPercentMaxCents),
      bonusRubies: getCurrentReward('bonusRubies', offer),
      fixedBonusMoney: getCurrentReward('fixedBonusMoney', offer),
      fixedFeatureTriggers: getCurrentReward('fixedFeatureTriggers', offer),
      fixedFreebet: getCurrentReward('fixedFreebet', offer),
      fixedFreeGamesVoucher: getCurrentReward('fixedFreeGamesVoucher', offer),
      fixedFreespins: getCurrentReward('fixedFreespins', offer),
      fixedLotteryBundles: getCurrentReward('fixedLotteryBagItems', offer),
      maxFixedFeatureTriggers: offer.fixedFeatureTriggers && Math.max.apply(Math, toConsumableArray(offer.fixedFeatureTriggers)),
      maxFixedFreebet: offer.fixedFreebet && Math.max.apply(Math, toConsumableArray(offer.fixedFreebet)),
      maxFixedFreeGamesVoucher: offer.fixedFreeGamesVoucher && Math.max.apply(Math, toConsumableArray(offer.fixedFreeGamesVoucher)),
      maxFixedFreespins: offer.fixedFreespins && Math.max.apply(Math, toConsumableArray(offer.fixedFreespins)),
      maxFixedLotteryBundles: offer.fixedLotteryBagItems && Math.max.apply(Math, toConsumableArray(offer.fixedLotteryBagItems)),
      maxFixedRubies: offer.bonusRubies && Math.max.apply(Math, toConsumableArray(offer.bonusRubies)),
      maxFixedBonusMoney: offer.fixedBonusMoney && Math.max.apply(Math, toConsumableArray(offer.fixedBonusMoney))
    };
  }, offers);
}

/**
 * Gets offers calculated by amount sent in
 * @memberof rushplay/offers
 * @param {Object} state Offer state
 * @param {Object} props
 * @param {number} props.amountCents amount in cents
 */
var getCalculatedOffers = createSelector([_getOffers, _getAmountCents, _getIsCampaign], _getCalculatedOffers);

/**
 * Gets calculated offer based on choice
 * @memberof rushplay/offers
 * @param {Object} state Offer state
 * @param {Object} props
 * @param {number} props.amountCents amount in cents
 * @param {number} props.choice choice id
 */
var getWelcomeOfferReward = createSelector([getCalculatedOffers, _getChoice], function (offers, choice) {
  return find(propEq('id', choice), offers);
});

export { updateDepositOffers, clear, fetchDepositOffers$1 as fetchDepositOffers, getValueIndexInRange, getMaxRewardsTranslationVariables, getOfferRewards, reducer, getCalculatedOffers, getWelcomeOfferReward, DEPOSIT_OFFERS_UPDATED, CLEARED };
